/**
 * House rules, rendered from the game's own declaration.
 *
 * Nothing here knows what game it is showing. A game declares an option as a
 * toggle, a choice or a bounded number; this draws the matching control and
 * sends the change back. Adding a house rule is therefore a server deploy and
 * nothing else — the same bargain the layout system already makes for the
 * board itself.
 *
 * That is also why there are exactly three kinds. Anything that does not fit
 * one of them is a sign the game wants to be a different game, not that this
 * file needs a fourth branch.
 */

import type { MatchView } from '../net/match.js';
import type { VariantSpecWire } from '../net/session.js';
import { SettingRow, SettingShell } from './SettingRow.js';

/*
 * A house rule that is on or off is a setting row, and always was.
 *
 * This drew a bare checkbox with its help running on after an em dash, sitting
 * in a column of rows that each had a mark, a switch and the word On beside
 * it. Nothing about a house rule makes it a different kind of thing from "let
 * people talk" - it was only written earlier.
 *
 * No mark: the four room settings have one each because there are four fixed
 * ones to draw. A house rule is whatever a game declares, and inventing an
 * icon per rule is not something a generic renderer can do.
 */
function Toggle({
  spec,
  value,
  onChange,
}: {
  spec: VariantSpecWire;
  value: boolean;
  onChange: (value: boolean) => void;
}) {
  return (
    <SettingRow title={spec.label} help={spec.help ?? ''} checked={value} onChange={onChange} />
  );
}

function Choice({
  spec,
  value,
  onChange,
}: {
  spec: VariantSpecWire;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <SettingShell title={spec.label} help={spec.help}>
      <div className="segmented" role="group" aria-label={spec.label}>
        {(spec.options ?? []).map((option) => (
          <button
            key={option.value}
            type="button"
            className={`segment${option.value === value ? ' chosen' : ''}`}
            onClick={() => onChange(option.value)}
            aria-pressed={option.value === value}
          >
            {option.label}
          </button>
        ))}
      </div>
    </SettingShell>
  );
}

/**
 * A stepper, not a text field.
 *
 * The bounds and the step come from the declaration, so every value this can
 * produce is one the server will accept. Offering a free-text number and
 * refusing it afterwards is the failure mode this avoids: Nakama turns a
 * thrown error into "Internal Server Error" and the reason never arrives.
 */
function Stepper({
  spec,
  value,
  onChange,
}: {
  spec: VariantSpecWire;
  value: number;
  onChange: (value: number) => void;
}) {
  const min = spec.min ?? 0;
  const max = spec.max ?? 0;
  const step = spec.step && spec.step > 0 ? spec.step : 1;

  const clamp = (next: number) => Math.min(max, Math.max(min, next));

  return (
    <SettingShell title={spec.label} help={spec.help}>
      <div className="stepper">
        <button
          type="button"
          className="step-button"
          disabled={value <= min}
          onClick={() => onChange(clamp(value - step))}
          aria-label={`Fewer ${spec.label}`}
        >
          −
        </button>
        <span className="stepper-value">{value}</span>
        <button
          type="button"
          className="step-button"
          disabled={value >= max}
          onClick={() => onChange(clamp(value + step))}
          aria-label={`More ${spec.label}`}
        >
          +
        </button>
      </div>
    </SettingShell>
  );
}

export function VariantControls({
  specs,
  view,
  onHostAction,
}: {
  specs: VariantSpecWire[];
  view: MatchView;
  onHostAction: (action: Record<string, unknown>) => void;
}) {
  if (specs.length === 0) return null;

  const set = (variantId: string, value: boolean | string | number) =>
    onHostAction({ action: 'SET_VARIANT', variantId, value });

  return (
    <div className="variant-controls">
      <span className="setting-group-label">House rules</span>
      {specs.map((spec) => {
        /*
         * The table's chosen value, falling back to the declared default.
         *
         * A room opened before this variant existed has no value stored for
         * it, and the server resolves exactly the same way — so the control
         * shows what the game will actually play.
         */
        const chosen = view.variants[spec.id] ?? spec.default;

        if (spec.kind === 'toggle') {
          return (
            <Toggle
              key={spec.id}
              spec={spec}
              value={chosen === true}
              onChange={(value) => set(spec.id, value)}
            />
          );
        }

        if (spec.kind === 'choice') {
          return (
            <Choice
              key={spec.id}
              spec={spec}
              value={String(chosen)}
              onChange={(value) => set(spec.id, value)}
            />
          );
        }

        return (
          <Stepper
            key={spec.id}
            spec={spec}
            value={typeof chosen === 'number' ? chosen : 0}
            onChange={(value) => set(spec.id, value)}
          />
        );
      })}
    </div>
  );
}
